import { Alert, AlertContent, AlertText } from "@hortis/ui/alert";
import { Button, IconButton } from "@hortis/ui/button";
import { ContextMenu, ContextMenuTrigger } from "@hortis/ui/context-menu";
import { Dot } from "@hortis/ui/dot";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import {
  AlertCircle,
  FilterLines,
  Map1,
  Map2,
  Plus,
  SearchMd,
  Settings4,
} from "@hortis/ui/icons";
import { MarkerPinOff1 } from "@hortis/ui/icons/marker-pin-off-1";
import { IUCNRedListTag } from "@hortis/ui/iucn-red-list-tag";
import { Label } from "@hortis/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { ScrollArea, ScrollAreaViewport } from "@hortis/ui/scroll-area";
import { Skeleton } from "@hortis/ui/skeleton";
import { Switch } from "@hortis/ui/switch";
import { Tag } from "@hortis/ui/tag";
import { Link, getRouteApi } from "@tanstack/react-router";
import type { Row } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import {
  type MapMaterialListFieldsFragment,
  PlantMaterialStatus,
  type PlantMaterialsOrderByInput,
  PlantMaterialsSearchColumn,
  type PlantMaterialsSearchTerm,
} from "generated/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectAllKeyboardShortcuts } from "src/components/data-table/data-table";
import { useTable } from "src/components/data-table/use-table";
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateHeader,
  EmptyStateTitle,
} from "src/components/empty-state";
import { FeaturedIcon } from "src/components/featured-icon/featured-icon";
import { ListFooter } from "src/components/list/list-footer";
import { useTopbarStore } from "src/components/nav-layout/topbar/topbar-store";
import { PlantMaterialStatusToColor } from "src/components/status-chip";
import MaterialsActionMenu from "src/features/actions-menu/variants/materials-action-menu";
import { useDownloadCsv } from "src/features/collection/components/download-accessions/use-download-csv";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { SearchBar } from "src/features/collection/components/search-bar/search-bar";
import { MaterialsContextMenuContent } from "src/features/context-menu/variants/materials-context-menu";
import { useCreateAccessionStore } from "src/features/create-accession/components/create-accession-store";
import {
  AddPlantMaterialFilter,
  type MaterialFilterType,
  PlantMaterialsFilters,
} from "src/features/filters/plant-materials";
import { FilterActionTypes } from "src/features/filters/plant-materials/filter-reducer";
import { MaterialFilterModals } from "src/features/filters/plant-materials/modals/filter-modals";
import { MaterialsSortSelect } from "src/features/filters/plant-materials/sort-select";
import type { MaterialFilters } from "src/features/filters/plant-materials/use-material-filters";
import { useAccessRole } from "src/features/permissions/use-access-role";
import {
  RecordCard,
  RecordCardContent,
  RecordCardPhoto,
  RecordCardProperties,
  RecordCardProperty,
  RecordCardTitle,
} from "src/features/records/components/new-record-card";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useIsScrolled } from "src/utils/hooks/scrolled";
import {
  getSelectedRowCount,
  getSelectedRowIds,
  useSelect,
} from "src/utils/hooks/tables/select-mode";
import { useToggle } from "src/utils/hooks/toggle";
import { PageDirection, createRelayParams } from "src/utils/pagination";
import { getThumbnailPhotoUrl } from "src/utils/photos";
import { twMerge } from "tailwind-merge";
import { getMapListMaterialsDocument } from "../../graphql";
import { useFindOnMap } from "../../hooks";
import { mapId, searchMenuLabels } from "../../utils";
import { useHoveredListMaterialStore } from "../hovered-list-material-store";

const ErrorAlert = () => (
  <Alert color="error" data-cy="error-alert" className="mx-3 my-2">
    <AlertCircle />
    <AlertContent>
      <AlertText>Something went wrong</AlertText>
    </AlertContent>
  </Alert>
);

const ListItem = ({
  row,
  selectMode,
  onClick,
}: {
  row: Row<MapMaterialListFieldsFragment>;
  selectMode: boolean;
  onClick: (row: Row<MapMaterialListFieldsFragment>) => void;
}) => {
  const { setHoveredListMaterial } = useHoveredListMaterialStore();
  const isNotMobile = useMediaQuery("sm");
  const handleMouseEnter = useCallback(() => {
    setHoveredListMaterial({
      id: row.original.id,
      materialQualifier: row.original.qualifier,
      accessionNumber: row.original.accession?.accessionNumber ?? "",
    });
  }, [row, setHoveredListMaterial]);

  const handleMouseLeave = useCallback(() => {
    setHoveredListMaterial(undefined);
  }, [setHoveredListMaterial]);

  const selected = row.getIsSelected();
  const material = row.original;

  const content = (
    <RecordCard
      size="sm"
      className={twMerge(
        material.status === PlantMaterialStatus.Absent && "grayscale",
      )}
      testId="material-record-card"
      selected={selected}
    >
      <RecordCardPhoto photo={getThumbnailPhotoUrl(material)} />
      <RecordCardContent className="truncate">
        <RecordCardTitle className="truncate">
          <span className="truncate shrink-0">
            {material.accession?.taxon?.scientificName}
          </span>
          <RecordCardProperty className="inline">
            {material.accession?.taxon?.commonName}
          </RecordCardProperty>
        </RecordCardTitle>
        <RecordCardProperties>
          {material.accession?.taxon != null && (
            <RecordCardProperty className="gap-1.5">
              <Dot color={PlantMaterialStatusToColor[material.status]} />
              {createMaterialNumber(
                material.accession?.accessionNumber,
                material.qualifier,
                "/",
              )}
            </RecordCardProperty>
          )}
          {material.collectionSiteLocation != null && (
            <RecordCardProperty className="max-w-40">
              <Map2 className="h-4 w-4 shrink-0 text-grey-500" />
              <span className="truncate">
                {material.collectionSiteLocation?.code}
              </span>
            </RecordCardProperty>
          )}
          {material.position == null && (
            <MarkerPinOff1
              className="h-4 w-4 shrink-0 text-grey-500"
              data-cy="missing-position-icon"
            />
          )}
          {material.accession?.taxon?.sharedScientificName?.taxon
            ?.globalRedListStatus != null &&
            material.accession.taxon.fullyValidated === true && (
              <IUCNRedListTag
                status={
                  material.accession?.taxon.sharedScientificName?.taxon
                    ?.globalRedListStatus
                }
                short
                className="text-xs py-px px-1 rounded-md"
              />
            )}
          <Tag className="border-grey-100 bg-grey-100 text-xs py-px px-1 rounded-md">
            {material.accession?.provenanceCategoryCode}
          </Tag>
        </RecordCardProperties>
      </RecordCardContent>
    </RecordCard>
  );

  return selectMode ? (
    <button
      type="button"
      className={twMerge(
        "w-full focus-visible:[&>div]:bg-grey-50 outline-none data-[state=open]:[&>div]:bg-grey-50",
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => {
        row.toggleSelected();
      }}
      data-cy="map-list-material-row"
    >
      {content}
    </button>
  ) : (
    <Link
      from="/site/$siteSlug/map"
      to="/site/$siteSlug/map/material/$materialNumber"
      onClick={() => {
        onClick(row);
      }}
      params={{
        materialNumber: createMaterialNumber(
          row.original.accession?.accessionNumber ?? "",
          row.original.qualifier,
        ),
      }}
      className={twMerge(
        "focus-visible:[&>div]:bg-grey-50 outline-none data-[state=open]:[&>div]:bg-grey-50",
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-cy="map-list-material-row"
    >
      {content}
    </Link>
  );
};

type SearchField = keyof typeof searchMenuLabels;

interface MapMaterialListProps {
  handleSearchSubmit: (searchValue: string, searchField: SearchField) => void;
  handleSearchClear: (field: PlantMaterialsSearchColumn) => void;
  setIsContextMenuOpen: (val: boolean) => void;
  searchTerm: PlantMaterialsSearchTerm | undefined;
  cursor: string | null;
  setCursor: (val: string) => void;
  materialFilters: MaterialFilters;
  orderBy: PlantMaterialsOrderByInput | undefined;
  setOrderBy: (val: PlantMaterialsOrderByInput | undefined) => void;
  downloadRequested: boolean;
  resetDownloadRequested: () => void;
}

const routeApi = getRouteApi("/_layout/site/$siteSlug/map");

const columns = [
  createColumnHelper<MapMaterialListFieldsFragment>().accessor("id", {}),
];

export const MapMaterialList = ({
  searchTerm,
  handleSearchClear,
  handleSearchSubmit,
  setIsContextMenuOpen,
  cursor,
  setCursor,
  materialFilters: filters,
  orderBy,
  downloadRequested,
  resetDownloadRequested,
  setOrderBy, // eslint-disable-next-line sonarjs/cognitive-complexity
}: MapMaterialListProps) => {
  const { siteSlug } = routeApi.useParams();
  const { subdomain } = routeApi.useRouteContext();
  const { setCreateAccessionDialogOpen } = useCreateAccessionStore();
  const { canEdit } = useAccessRole();
  const findOnMap = useFindOnMap(mapId);
  const [dir, setDir] = useState(PageDirection.Next);
  const { attachRef, ref: containerRef } = useIsScrolled();
  const [loadingDirection, setLoadingDirection] =
    useState<PageDirection | null>(null);
  const [actionsMenuOpen, toggleActionsMenuOpen] = useToggle(false);

  const { data, error, isFetching, isPending } = useGqlQuery({
    query: getMapListMaterialsDocument,
    variables: {
      collectionSiteSlug: siteSlug,
      organisationSubdomain: subdomain,
      filters: filters.preparedFilters,
      orderBy,
      searchTerm,
      ...createRelayParams({ cursor, dir, rowsPerPage: 30 }),
    },
  });
  const loading = isFetching || isPending;

  const setExcludeMaterialFilter = (checked: boolean) => {
    filters.dispatchFilters({
      type: FilterActionTypes.MODIFY_ROOT_FILTER,
      payload: { excludeAbsentMaterial: checked },
    });
  };

  const pageInfo = data?.org?.site?.result?.pageInfo;
  const materials = data?.org?.site?.result?.nodes;
  const total = data?.org?.site?.total;

  const { table } = useTable({
    columns: columns,
    data: useMemo(() => (materials == null ? [] : [...materials]), [materials]),
    tableId: "site-materials-table",
  });
  useSelectAllKeyboardShortcuts(table);

  const { selectMode, toggleSelectMode } = useSelect({
    resetRowSelection: table.resetRowSelection,
    filterCounts: filters.filterCounts,
    sort: orderBy,
    search: searchTerm,
  });

  const onPrevPage = () => {
    if (pageInfo?.startCursor != null && pageInfo.hasPreviousPage) {
      setCursor(pageInfo.startCursor);
      setDir(PageDirection.Prev);
    }
  };

  const onNextPage = () => {
    if (pageInfo?.endCursor != null && pageInfo.hasNextPage) {
      setCursor(pageInfo.endCursor);
      setDir(PageDirection.Next);
    }
  };

  const clearSearch = () => {
    if (searchTerm != null) {
      handleSearchClear(searchTerm.field);
    }
  };

  const handleDownload = useDownloadCsv({
    csvName: "plant-materials",
    path: "/download/materials",
    body: {
      searchTerm,
      filters: {
        ...filters.preparedFilters,
        id: {
          in: table
            .getSelectedRowModel()
            .rows.map(({ original }) => original.id),
        },
      },
    },
  });

  useEffect(() => {
    if (downloadRequested) {
      void handleDownload();
      resetDownloadRequested();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- due to body prop, handleDownload does not have stable identity
  }, [downloadRequested, resetDownloadRequested]);

  useEffect(() => {
    if (!isFetching) {
      if (containerRef != null) {
        containerRef.scrollTop = 0;
      }
      setLoadingDirection(null);
    }
  }, [isFetching, containerRef]);

  const [openFilter, setOpenFilter] = useState<MaterialFilterType | false>(
    false,
  );

  const searchActive = searchTerm != null && searchTerm.value !== "";
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const setTitle = useTopbarStore((state) => state.setTitle);

  const handleRowClick = useCallback(
    (row: Row<MapMaterialListFieldsFragment>) => {
      findOnMap(row.original.position);
    },
    [findOnMap],
  );

  const pageTitle = "Map";
  setTitle(pageTitle);

  const selectedRecordsIDs = getSelectedRowIds(table.getState().rowSelection);
  const selectedRecordCount = getSelectedRowCount(
    table.getState().rowSelection,
  );

  return (
    <>
      <div className="flex w-full gap-2 border-b border-grey-200 px-4 py-4 lg:px-6">
        <SearchBar
          testId="map-list-search-form"
          className="flex-1"
          menuLabels={searchMenuLabels}
          handleSubmitCallback={handleSearchSubmit}
          handleClearCallback={handleSearchClear}
          searchTermOverride={searchTerm}
          searchFieldIfNumber={PlantMaterialsSearchColumn.MaterialNumber}
        />
        <AddPlantMaterialFilter
          setOpenFilter={(type) => {
            setOpenFilter(type.filterType);
          }}
          align="center"
          iconButton
          buttonProps={{
            size: "sm",
            testId: "add-filter-button",
            icon: FilterLines,
            ref: filterButtonRef,
            className: "border",
            variant: "secondaryGray",
          }}
        />
        <MaterialFilterModals
          openFilter={openFilter}
          handleFilterModalClose={() => {
            setOpenFilter(false);
          }}
          filterModalAnchorEl={filterButtonRef}
          dispatchFilters={filters.dispatchFilters}
        />
        <Popover>
          <PopoverTrigger asChild>
            <IconButton
              icon={Settings4}
              ariaLabel="View settings"
              size="sm"
              testId="view-settings-button"
            />
          </PopoverTrigger>
          <PopoverContent className="min-w-44">
            <div className="border-b border-grey-200 px-3.5 py-4">
              <MaterialsSortSelect
                fullWidth
                buttonProps={{ size: "xs" }}
                orderBy={orderBy}
                updateOrderBy={setOrderBy}
              />
            </div>
            <Label className="flex items-center justify-between px-3.5 py-4">
              Show absent
              <Switch
                data-cy="show-absent-toggle"
                size="sm"
                onCheckedChange={(checked) => {
                  setExcludeMaterialFilter(!checked);
                }}
                checked={filters.filters.excludeAbsentMaterial !== true}
              />
            </Label>
          </PopoverContent>
        </Popover>
      </div>
      {filters.filterCounts.total - filters.filterCounts.excludeAbsentMaterial >
        0 && (
        <div className="border-b border-grey-200 px-4 py-4 lg:px-6">
          <PlantMaterialsFilters
            hideAbsentFilter
            excludeAbsentPlantMaterial={
              filters.filters.excludeAbsentMaterial === true
            }
            onChange={setExcludeMaterialFilter}
            filters={filters.filters}
            dispatchFilters={filters.dispatchFilters}
            resetFilters={filters.resetFilters}
            filterCount={filters.filterCounts.total}
            hideClear
          />
        </div>
      )}
      <ScrollArea className="flex flex-col flex-1">
        <ScrollAreaViewport className="flex-1 overflow-y-auto" ref={attachRef}>
          <div className="px-3 py-2 space-y-px">
            {((materials == null && !loading) || error != null) && (
              <ErrorAlert />
            )}
            {materials == null && loading && (
              <div className="space-y-6 px-3 py-4">
                {Array.from({ length: 14 }).map((_, index) => (
                  <div className="flex items-center gap-3">
                    <Skeleton className="size-12 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="w-44 h-5" />
                      <Skeleton className="w-36 h-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {table.getRowModel().rows.map((row) => (
              <ContextMenu onOpenChange={setIsContextMenuOpen} key={row.id}>
                <ContextMenuTrigger>
                  <ListItem
                    key={row.id}
                    row={row}
                    selectMode={selectMode}
                    onClick={handleRowClick}
                  />
                </ContextMenuTrigger>
                <MaterialsContextMenuContent
                  isTrash={false}
                  matSearch={searchTerm}
                  preparedFilters={filters.preparedFilters}
                  clickedRow={row.original}
                  selectedRecordsIDs={selectedRecordsIDs}
                  onRequestDownload={handleDownload}
                />
              </ContextMenu>
            ))}
          </div>
          {materials != null && materials.length === 0 && (
            <EmptyState className="my-12 justify-self-center p-6 md:my-24">
              <EmptyStateHeader>
                <FeaturedIcon>
                  {searchActive ? (
                    <SearchMd className="h-5 w-5 text-primary-600" />
                  ) : (
                    <Map1 className="h-5 w-5 text-primary-600" />
                  )}
                </FeaturedIcon>
                <EmptyStateTitle>No materials found</EmptyStateTitle>
                <EmptyStateBody>
                  {searchTerm != null && searchTerm.value !== ""
                    ? `Your search “${
                        searchTerm.value
                      }” did not match any records. Please try again${
                        canEdit ? " or add material" : ""
                      }.`
                    : `Your mapped plant materials will live here.${
                        canEdit
                          ? " Start creating your map by accessioning your material."
                          : ""
                      }`}
                </EmptyStateBody>
              </EmptyStateHeader>
              <EmptyStateActions>
                {searchActive && (
                  <Button onClick={clearSearch}>Clear search</Button>
                )}
                {canEdit && (
                  <Button
                    variant="primary"
                    startIcon={Plus}
                    data-cy="new-accession-button"
                    onClick={() => {
                      setCreateAccessionDialogOpen(true);
                    }}
                  >
                    New accession
                  </Button>
                )}
              </EmptyStateActions>
            </EmptyState>
          )}
        </ScrollAreaViewport>
      </ScrollArea>
      <ListFooter
        selectedRowCount={selectedRecordCount}
        handleDownload={handleDownload}
        selectAllRows={() => {
          const currentPageRecordsCount =
            table.getSelectedRowModel().rows.length;
          table.toggleAllPageRowsSelected(currentPageRecordsCount <= 0);
        }}
        selectMode={selectMode}
        toggleSelectMode={toggleSelectMode}
        onActionsMenu={toggleActionsMenuOpen}
        total={total}
        onPreviousPage={onPrevPage}
        onNextPage={onNextPage}
        hasPreviousPage={pageInfo?.hasPreviousPage}
        hasNextPage={pageInfo?.hasNextPage}
        previousPageLoading={loadingDirection === PageDirection.Prev}
        nextPageLoading={loadingDirection === PageDirection.Next}
        testIdPrefix="plant-materials"
      />
      {materials != null && selectedRecordCount > 0 && (
        <MaterialsActionMenu
          selectedRecordsIDs={selectedRecordsIDs}
          open={actionsMenuOpen}
          toggleOpen={toggleActionsMenuOpen}
        />
      )}
    </>
  );
};
